import { useEffect } from "react";
import { Header } from "@/components/layout/Header";
import { MessageSquare, Brain, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import footerFunding from "@/assets/footer-funding.jpeg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEOPresets } from "@/components/SEO";

export default function FAQ() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const faqs = [
    {
      question: "What is GlobalCoders?",
      answer:
        "GlobalCoders is an AI-powered web and mobile application that helps professionals practice and improve their English through realistic business conversation simulations. It focuses on authentic workplace scenarios, practical vocabulary, and confident communication.",
    },
    {
      question: "Who is GlobalCoders for?",
      answer:
        "GlobalCoders is designed for non-native English-speaking professionals who want to communicate more effectively in international business settings — whether in meetings, presentations, negotiations, or casual workplace interactions.",
    },
    {
      question: "What kind of content does the app include?",
      answer:
        "The web and mobile application includes interactive business conversation simulations with AI, professional vocabulary, real-world workplace scenarios, pronunciation training, and personalized feedback.",
    },
    {
      question: "Do I need to be fluent in English to use GlobalCoders?",
      answer:
        "No! GlobalCoders is ideal for intermediate learners (level B1 and above) who want to feel more confident and natural when speaking English in professional situations.",
    },
    {
      question: "How is this different from regular English courses?",
      answer:
        "GlobalCoders focuses entirely on real business communication. Instead of generic topics, it helps you practice the kinds of conversations that happen in real work environments — like team meetings, client discussions, progress updates, and performance reviews.",
    },
    {
      question: "Is GlobalCoders only for developers?",
      answer:
        "No. While it was initially created for tech teams, GlobalCoders can help professionals from all industries improve their communication skills in business contexts.",
    },
    {
      question: "How much time should I spend on GlobalCoders per week?",
      answer:
        "You can make meaningful progress with just 5–10 minutes a day. The web and mobile application is designed for busy professionals who want to practice anytime — even between meetings or during a coffee break.",
    },
    {
      question: "Can I get feedback on my speaking?",
      answer:
        "Yes! The web and mobile app provide instant feedback on your pronunciation and on your spoken responses during business conversation simulations with AI.",
    },
    {
      question: "How much does it cost?",
      answer:
        "You can try three business conversation simulations on GlobalCoders completely free of charge. After that, you can upgrade to one of our paid plans to continue practicing.",
    },
    {
      question: "What kinds of scenarios does GlobalCoders prepare me for?",
      answer:
        "You'll practice conversations like giving project updates, running meetings, negotiating, presenting ideas, networking, and more — all in English.",
    },
    {
      question: "How can I access GlobalCoders?",
      answer:
        "GlobalCoders is available both as a web application and on iOS and Android — start practicing real business English conversations anytime, anywhere.",
    },
    {
      question: "How can I contact GlobalCoders?",
      answer:
        "For any questions, support, or feedback, you can email us at support@globalcoders.app. We'll get back to you soon!",
    },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-accent to-secondary">
      {SEOPresets.faq}
      <Header showLogout solidBackground={true} />

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2 text-center">
              Frequently Asked Questions
            </h1>
            <p className="text-center text-gray-600 mb-12">
              Everything you need to know about GlobalCoders
            </p>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 bg-white"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="font-semibold text-gray-900">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-secondary text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* About Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                About GlobalCoders
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Master business English through AI-powered conversation
                simulations. Practice real-world scenarios and receive instant
                feedback.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Quick Links
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/meeting-simulator"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Meeting Simulator
                  </Link>
                </li>
                <li>
                  <a
                    href="#features"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Resources
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/subscription-billing"
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    Subscription and Billing Terms
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold">Contact Us</h3>
              <div className="text-sm text-white/80 space-y-2">
                <p>Email: support@globalcoders.app</p>
                <p className="leading-relaxed">
                  Carrer de Joan Oliver, 22D, 2-1
                  <br />
                  Sant Cugat del Vallès
                  <br />
                  08172, Spain
                </p>
              </div>
            </div>
          </div>

          {/* Funding Logo Section */}
          <div className="border-t border-white/20 pt-8 mb-8">
            <div className="flex justify-center">
              <img
                src={footerFunding}
                alt="European Union Funding - Next Generation EU"
                className="h-20 object-contain opacity-90 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} SimuFluent by Sundae Education
              Technologies SL. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
